import { isLosslessNumber } from "@/lib/lossless"
import syncMapping from "../../../../fixtures/dev/order-match-sync/recon.shopify_erpnext_orders.mapping.json"

/**
 * The sync the order-match workbook promotes: MappingIR generated by the Norn TypeScript SDK from
 * tools/norn-ts/fixtures/order-match-sync/recon.shopify_erpnext_orders.mapping.ts (golden-tested
 * byte for byte and admitted by the Rust checker in CI). The console never writes IR by hand; it
 * checks this artifact against the cell's installed definitions and records the promotion.
 */
export const SYNC_MAPPING: Record<string, unknown> = syncMapping as Record<string, unknown>
export const SYNC_SOURCE = "tools/norn-ts/fixtures/order-match-sync/recon.shopify_erpnext_orders.mapping.ts"

/** Sorted-key canonical JSON, exact numbers kept as their digits (the checker's artifact hash input). */
export function canonical(value: unknown): string {
  if (isLosslessNumber(value)) return value.value
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`
  if (value && typeof value === "object")
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical((value as Record<string, unknown>)[key])}`)
      .join(",")}}`
  return JSON.stringify(value)
}

/** The envelope hash the Rust checker pins artifacts by (`norn.canonical.json/norn1`). */
export async function artifactSha256(artifact: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(`norn.canonical.json/norn1\0${canonical(artifact)}`)
  const digest = await crypto.subtle.digest("SHA-256", bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("")
}

export interface PromoteCounts {
  readonly matched: number
  readonly needsReview: number
  readonly unmatched: number
}

/** A sync a workbook can promote: its id, systems and the SDK-generated MappingIR it would run. */
export interface SyncSpec {
  readonly syncId: string
  readonly sourceSystem: string
  readonly targetSystem: string
  readonly mapping: Record<string, unknown>
  /** The TypeScript the mapping was generated from. */
  readonly source: string
}

export const ORDER_SYNC: SyncSpec = { syncId: "shopify-erpnext-orders", sourceSystem: "Shopify", targetSystem: "ERPNext", mapping: SYNC_MAPPING, source: SYNC_SOURCE }

/** The recon.order_sync.promote input for one sync's mapping, its hash and the checker's verdict. */
export function syncInput(spec: SyncSpec, sha256: string, admitted: boolean, counts: PromoteCounts, rules: string): Record<string, unknown> {
  return {
    sync_id: spec.syncId,
    title: String(spec.mapping.name ?? spec.syncId),
    source_system: spec.sourceSystem,
    target_system: spec.targetSystem,
    mapping_id: String(spec.mapping.artifactId),
    mapping_version: String(spec.mapping.version),
    mapping_sha256: sha256,
    admitted,
    status: "proposed",
    rules,
    matched: counts.matched,
    needs_review: counts.needsReview,
    unmatched: counts.unmatched,
  }
}

/** The order workbook's promote input (its one sync). */
export function promoteInput(sha256: string, admitted: boolean, counts: PromoteCounts, rules: string): Record<string, unknown> {
  return syncInput(ORDER_SYNC, sha256, admitted, counts, rules)
}
